import React, { useRef, useMemo } from 'react';
import { useReactToPrint } from 'react-to-print';
import html2canvas from 'html2canvas';
import { Printer, Download, X, CheckCircle } from 'lucide-react';

interface ReceiptModalProps {
    isOpen: boolean;
    onClose: () => void;
    type: 'sale' | 'subscription' | 'coaching' | 'payment' | 'seance';
    data: any;
}

const ReceiptModal: React.FC<ReceiptModalProps> = ({ isOpen, onClose, type, data }) => {
    const componentRef = useRef<HTMLDivElement>(null);

    const handlePrint = useReactToPrint({
        contentRef: componentRef,
        documentTitle: `Recu_HopFitness_${new Date().getTime()}`
    });

    // Numéro de reçu stable pour toute la durée d'ouverture de ce reçu
    const receiptNumber = useMemo(() => {
        if (!isOpen) return '';
        if (data?.receiptNumber) return String(data.receiptNumber);
        return Math.floor(100000 + Math.random() * 900000).toString();
    }, [isOpen, data?.receiptNumber]);

    // Date stable du reçu
    const receiptDate = useMemo(() => {
        if (!isOpen) return '';
        return new Date().toLocaleString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    }, [isOpen]);

    if (!isOpen) return null;

    // Normalisation du montant total
    const rawTotal = data?.total ?? data?.amount ?? data?.total_amount ?? 0;
    const totalNum = Number(rawTotal) || 0;
    const formattedTotal = totalNum.toLocaleString('fr-FR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
    });

    // Récupération du nom et téléphone client
    const clientName = data?.clientName || 
        (data?.client ? `${data.client.first_name || ''} ${data.client.last_name || ''}`.trim() : '') || 
        '';
    const clientPhone = data?.clientPhone || data?.phone || data?.client?.phone || data?.parentPhone || '';

    // Nettoyage du numéro de téléphone pour WhatsApp (ex: indicatif Côte d'Ivoire 225)
    const getCleanPhoneForWhatsApp = (raw?: string): string => {
        if (!raw) return '';
        let digits = String(raw).replace(/\D/g, '');
        if (!digits) return '';
        // Format CI à 10 chiffres débutant par 0 (ex: 0712345678, 05..., 01...)
        if (digits.length === 10 && digits.startsWith('0')) {
            return `225${digits}`;
        }
        // Format débutant déjà par 225
        if (digits.startsWith('225') && digits.length >= 12) {
            return digits;
        }
        // Format international générique
        if (digits.length >= 8) {
            return digits;
        }
        return '';
    };

    // Construction du message WhatsApp
    const generateWhatsAppMessage = () => {
        let detailsText = '';

        if (type === 'sale' && data?.items) {
            detailsText = data.items
                .map((it: any) => `• ${it.quantity}x ${it.name} : ${(Number(it.price || 0) * Number(it.quantity || 1)).toLocaleString('fr-FR')} FCFA`)
                .join('\n');
        } else if (type === 'subscription') {
            const lines = [
                `• Forfait : ${data.planName || data.forfaitName || 'Abonnement'}`,
                data.periodicity ? `• Périodicité : ${data.periodicity}` : '',
                data.duration ? `• Durée : ${data.duration}` : '',
                data.startDate ? `• Début : ${data.startDate}` : '',
                data.endDate ? `• Fin : ${data.endDate}` : '',
                data.quantity && data.quantity > 1 ? `• Quantité : x${data.quantity}` : '',
                data.paymentMethod ? `• Mode de paiement : ${data.paymentMethod}` : '',
                data.hasInscription ? `• Frais d'inscription inclus` : ''
            ].filter(Boolean);
            detailsText = lines.join('\n');
        } else if (type === 'seance') {
            const lines = [
                `• Séance : ${data.forfaitName || 'Accès Séance'}`,
                data.sessionDate ? `• Date : ${data.sessionDate}` : '',
                data.sessionTime ? `• Heure : ${data.sessionTime}` : '',
                data.paymentMethod ? `• Mode de paiement : ${data.paymentMethod}` : ''
            ].filter(Boolean);
            detailsText = lines.join('\n');
        } else if (type === 'coaching') {
            detailsText = `• Formule Coaching : ${data.formulaName || 'Coaching personnalisé'}`;
        } else {
            detailsText = `• Règlement / Transaction effectuée`;
        }

        return `🏋️ *HOPFITNESS - REÇU DE PAIEMENT*\n` +
            `━━━━━━━━━━━━━━━━━━━━\n` +
            `📅 Date : ${receiptDate}\n` +
            `🧾 Reçu N° : #${receiptNumber}\n` +
            (clientName ? `👤 Client : ${clientName}\n` : '') +
            (clientPhone ? `📞 Tél : ${clientPhone}\n` : '') +
            `━━━━━━━━━━━━━━━━━━━━\n` +
            `📋 *DÉTAILS :*\n` +
            `${detailsText}\n` +
            `━━━━━━━━━━━━━━━━━━━━\n` +
            `💰 *TOTAL TTC : ${formattedTotal} FCFA*\n` +
            `━━━━━━━━━━━━━━━━━━━━\n` +
            `📍 HOP FITNESS - Centre de remise en forme\n` +
            `✨ Merci pour votre confiance !`;
    };

    // Redirection directe vers WhatsApp
    const handleShareWhatsApp = () => {
        const text = generateWhatsAppMessage();
        const phone = getCleanPhoneForWhatsApp(clientPhone);

        let whatsappUrl = '';
        if (phone) {
            whatsappUrl = `https://api.whatsapp.com/send?phone=${phone}&text=${encodeURIComponent(text)}`;
        } else {
            whatsappUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(text)}`;
        }

        // Ouverture de WhatsApp
        window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
    };

    const handleDownloadImage = async () => {
        if (componentRef.current) {
            try {
                const canvas = await html2canvas(componentRef.current, { scale: 2 });
                const image = canvas.toDataURL('image/png', 1.0);
                const link = document.createElement('a');
                link.download = `recu_hopfitness_${receiptNumber}.png`;
                link.href = image;
                link.click();
            } catch (error) {
                console.error("Erreur lors de la génération de l'image", error);
                alert("Erreur lors du téléchargement.");
            }
        }
    };

    const renderItems = () => {
        if (type === 'sale' && data?.items) {
            return data.items.map((item: any, idx: number) => (
                <div key={idx} className="flex justify-between text-xs py-1 gap-2">
                    <span className="text-gray-700 truncate">{item.quantity}x {item.name}</span>
                    <span className="font-semibold whitespace-nowrap">
                        {(Number(item.price || 0) * Number(item.quantity || 1)).toLocaleString('fr-FR')} FCFA
                    </span>
                </div>
            ));
        } else if (type === 'subscription') {
            return (
                <div className="space-y-1 text-xs">
                    <div className="flex justify-between py-0.5 gap-2">
                        <span className="text-gray-600">Forfait :</span>
                        <span className="font-bold text-gray-900 text-right">{data.planName || data.forfaitName}</span>
                    </div>
                    {data.periodicity && (
                        <div className="flex justify-between py-0.5 gap-2">
                            <span className="text-gray-600">Périodicité :</span>
                            <span className="font-medium text-gray-800 text-right">{data.periodicity}</span>
                        </div>
                    )}
                    {data.duration && (
                        <div className="flex justify-between py-0.5 gap-2">
                            <span className="text-gray-600">Durée :</span>
                            <span className="font-medium text-gray-800 text-right">{data.duration}</span>
                        </div>
                    )}
                    {data.startDate && (
                        <div className="flex justify-between py-0.5 gap-2">
                            <span className="text-gray-600">Début :</span>
                            <span className="font-medium text-gray-800 text-right">{data.startDate}</span>
                        </div>
                    )}
                    {data.endDate && (
                        <div className="flex justify-between py-0.5 gap-2">
                            <span className="text-gray-600">Fin :</span>
                            <span className="font-medium text-gray-800 text-right">{data.endDate}</span>
                        </div>
                    )}
                    {data.quantity && data.quantity > 1 && (
                        <div className="flex justify-between py-0.5 gap-2">
                            <span className="text-gray-600">Quantité :</span>
                            <span className="font-medium text-gray-800 text-right">x{data.quantity}</span>
                        </div>
                    )}
                    {data.paymentMethod && (
                        <div className="flex justify-between py-0.5 gap-2">
                            <span className="text-gray-600">Paiement :</span>
                            <span className="font-medium text-gray-800 text-right">{data.paymentMethod}</span>
                        </div>
                    )}
                    {data.hasInscription && (
                        <div className="flex justify-between py-0.5 gap-2">
                            <span className="text-emerald-700 font-semibold">Frais d'inscription inclus</span>
                            <span className="font-semibold text-emerald-700">✓</span>
                        </div>
                    )}
                </div>
            );
        } else if (type === 'seance') {
            return (
                <div className="space-y-1 text-xs">
                    <div className="flex justify-between py-0.5 gap-2">
                        <span className="text-gray-600">Séance :</span>
                        <span className="font-bold text-gray-900 text-right">{data.forfaitName}</span>
                    </div>
                    {data.sessionDate && (
                        <div className="flex justify-between py-0.5 gap-2">
                            <span className="text-gray-600">Date Séance :</span>
                            <span className="font-medium text-gray-800 text-right">{data.sessionDate}</span>
                        </div>
                    )}
                    {data.sessionTime && (
                        <div className="flex justify-between py-0.5 gap-2">
                            <span className="text-gray-600">Heure :</span>
                            <span className="font-medium text-gray-800 text-right">{data.sessionTime}</span>
                        </div>
                    )}
                    {data.paymentMethod && (
                        <div className="flex justify-between py-0.5 gap-2">
                            <span className="text-gray-600">Paiement :</span>
                            <span className="font-medium text-gray-800 text-right">{data.paymentMethod}</span>
                        </div>
                    )}
                </div>
            );
        } else if (type === 'coaching') {
            return (
                <div className="flex justify-between text-xs py-1 gap-2">
                    <span className="text-gray-600">Coaching :</span>
                    <span className="font-bold text-gray-900 text-right">{data.formulaName}</span>
                </div>
            );
        } else if (type === 'payment') {
            return (
                <div className="flex justify-between text-xs py-1 gap-2">
                    <span className="text-gray-600">Opération :</span>
                    <span className="font-bold text-gray-900 text-right">Règlement / Prolongation</span>
                </div>
            );
        }
        return null;
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-auto overflow-hidden flex flex-col max-h-[92vh]">
                {/* Header */}
                <div className="px-5 py-3.5 border-b border-gray-100 flex justify-between items-center bg-gray-50/90">
                    <div className="flex items-center gap-2 text-emerald-600">
                        <CheckCircle size={20} className="shrink-0" />
                        <h3 className="text-base sm:text-lg font-bold text-gray-800">Transaction Réussie</h3>
                    </div>
                    <button 
                        onClick={onClose} 
                        className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100 transition-colors"
                        aria-label="Fermer"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Ticket Body */}
                <div className="p-4 sm:p-6 overflow-y-auto flex-1 bg-gray-100/80 flex justify-center items-start">
                    <div 
                        ref={componentRef} 
                        className="bg-white p-5 sm:p-6 shadow-sm w-full max-w-[340px] border border-gray-200 print:shadow-none print:border-none print:p-0 rounded-sm"
                        style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace' }}
                    >
                        {/* En-tête du ticket */}
                        <div className="text-center mb-5">
                            <h2 className="text-2xl font-black text-brand-dark uppercase tracking-wider mb-1">HOPFITNESS</h2>
                            <p className="text-[11px] text-gray-500">Centre de remise en forme</p>
                            <p className="text-[11px] text-gray-500">123 Rue de la Forme, Ville</p>
                            <p className="text-[11px] text-gray-500">Tél: +123 456 789</p>
                        </div>

                        {/* Metas */}
                        <div className="border-t border-b border-dashed border-gray-300 py-2.5 mb-3.5 space-y-1">
                            <div className="flex justify-between text-[11px]">
                                <span className="text-gray-500">Date:</span>
                                <span className="font-medium text-gray-800">{receiptDate}</span>
                            </div>
                            <div className="flex justify-between text-[11px]">
                                <span className="text-gray-500">Reçu N°:</span>
                                <span className="font-bold text-gray-800">#{receiptNumber}</span>
                            </div>
                            {clientName && (
                                <div className="flex justify-between text-[11px] gap-2">
                                    <span className="text-gray-500 shrink-0">Client:</span>
                                    <span className="font-bold text-gray-900 text-right truncate">{clientName}</span>
                                </div>
                            )}
                            {clientPhone && (
                                <div className="flex justify-between text-[11px]">
                                    <span className="text-gray-500">Tél:</span>
                                    <span className="text-gray-800">{clientPhone}</span>
                                </div>
                            )}
                        </div>

                        {/* Détails */}
                        <div className="mb-4">
                            <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">DÉTAILS</h4>
                            {renderItems()}
                        </div>

                        {/* Total TTC (Garanti sur une seule ligne avec nowrap et shrink-0) */}
                        <div className="border-t-2 border-dashed border-gray-300 pt-3.5 pb-1 mb-4">
                            <div className="flex justify-between items-center gap-2">
                                <span className="text-xs sm:text-sm font-extrabold text-gray-700 uppercase tracking-wider shrink-0">
                                    TOTAL TTC
                                </span>
                                <span className="text-base sm:text-lg font-black text-brand-dark whitespace-nowrap text-right">
                                    {formattedTotal} FCFA
                                </span>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="text-center text-[10px] text-gray-400 mt-6 leading-tight">
                            <p>Merci de votre confiance !</p>
                            <p className="mt-1">Les articles ne sont ni repris ni échangés.</p>
                        </div>
                    </div>
                </div>

                {/* Actions Bar */}
                <div className="p-3.5 sm:p-4 border-t border-gray-100 bg-white grid grid-cols-2 gap-2.5 sm:gap-3">
                    <button 
                        onClick={() => handlePrint()}
                        className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-800 font-semibold rounded-lg transition-colors text-sm shadow-sm active:scale-95"
                    >
                        <Printer size={18} className="shrink-0" />
                        <span>Imprimer</span>
                    </button>
                    
                    <button 
                        onClick={handleShareWhatsApp}
                        className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2.5 bg-[#25D366] hover:bg-[#20ba5a] text-white font-semibold rounded-lg transition-all text-sm shadow-sm hover:shadow active:scale-95"
                        title="Partager directement le reçu sur WhatsApp"
                    >
                        {/* Logo officiel WhatsApp SVG */}
                        <svg className="w-5 h-5 fill-current shrink-0" viewBox="0 0 24 24">
                            <path d="M12.031 6.172c-3.181 0-5.767 2.586-5.768 5.766-.001 1.298.38 2.27 1.019 3.287l-.582 2.128 2.182-.573c.978.58 1.911.928 3.145.929 3.178 0 5.767-2.587 5.768-5.766.001-3.187-2.575-5.77-5.764-5.771zm3.392 8.244c-.144.405-.837.774-1.17.824-.299.045-.677.063-1.092-.069-.252-.08-.575-.187-.988-.365-1.739-.751-2.874-2.502-2.961-2.617-.087-.116-.708-.94-.708-1.793s.448-1.273.607-1.446c.159-.173.346-.217.462-.217l.332.006c.106.005.249-.04.39.298.144.347.491 1.2.534 1.287.043.087.072.188.014.304-.058.116-.087.188-.173.289l-.26.304c-.087.086-.177.18-.076.354.101.174.449.741.964 1.201.662.591 1.221.774 1.394.86.174.086.275.072.376-.044.101-.116.433-.506.549-.68.116-.173.231-.145.39-.086s1.011.477 1.184.564.289.13.332.202c.043.073.043.419-.101.824zM12 2C6.477 2 2 6.477 2 12c0 1.891.524 3.66 1.434 5.176L2 22l4.957-1.398A9.956 9.956 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2zm0 18.182a8.156 8.156 0 01-4.309-1.226l-.309-.187-2.936.828.843-2.86-.203-.324A8.157 8.157 0 1112 20.182z"/>
                        </svg>
                        <span>Partager</span>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ReceiptModal;
